// src/components/Footer.js
"use client";

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaLinkedin, FaRegCopy, FaCheck } from "react-icons/fa";

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
    {children}
  </span>
);

const Tile = ({ title, children, className = "" }) => (
  <div className={`rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm ${className}`}>
    {title && (
      <h4 className="text-xs font-bold uppercase tracking-[0.12em] text-slate-900">
        {title}
      </h4>
    )}
    <div className={title ? "mt-4 text-sm text-slate-700" : ""}>{children}</div>
  </div>
);

/* Small, reusable email pill with copy button */
const EmailPill = ({ email }) => {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // ignore copy failures silently
    }
  };

  return (
    <div className="inline-flex items-center max-w-full rounded-xl border border-slate-300 bg-white">
      <a
        href={`mailto:${email}`}
        className="px-3 py-2 text-sm font-semibold text-slate-900 truncate"
        style={{ maxWidth: "calc(100% - 44px)" }} // reserve room for copy button
        title={email}
      >
        <span className="break-words">{email}</span>
      </a>
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy email"
        className="shrink-0 inline-flex h-10 w-11 items-center justify-center rounded-r-xl border-l border-slate-300 text-slate-700 hover:bg-slate-50"
      >
        {copied ? <FaCheck size={16} /> : <FaRegCopy size={16} />}
      </button>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="bg-white text-slate-900">
      {/* Hairline separator */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      {/* CTA card */}
      <div className="px-4 sm:px-6 md:px-10 pt-12">
        <div className="mx-auto max-w-7xl">
          <Tile className="text-center">
            <div className="flex flex-col items-center gap-3">
              <h2 className="heading-serif text-2xl sm:text-3xl font-bold">
                Have a property, project, or file to discuss?
              </h2>
              <p className="text-sm sm:text-base text-slate-600">
                Real Estate Advisory • Tech & Financial Consulting • Lending & Banking Investigations - Canada first, with global investors.
              </p>

              <div className="mt-2 flex flex-wrap justify-center gap-3">
                <Link
                  to="/listings"
                  className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 transition"
                >
                  View Listings
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-xl border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 transition"
                >
                  Contact
                </Link>
                <EmailPill email="info@maoqdigital.com" />
              </div>

              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Chip>Canada Focus</Chip>
                <Chip>Global Capital</Chip>
                <Chip>Private Process</Chip>
              </div>
            </div>
          </Tile>
        </div>
      </div>

      {/* Main grid */}
      <div className="px-4 sm:px-6 md:px-10 pt-10">
        <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Brand / Contact */}
          <div className="lg:col-span-5">
            <Tile>
              <div className="flex items-center gap-4">
                {/* Bigger logo, keep as image only */}
                <img
                  src="/logo.svg"
                  alt="Logo"
                  className="h-20 sm:h-24 md:h-28 w-auto object-contain"
                />
              </div>

              <p className="mt-4 text-sm sm:text-base text-slate-700">
                Advisory and consulting for real estate, technology, and finance -
                hotels, plazas, and land; tech & financial industry consulting;
                Clean information, steady process, clear outcomes.
              </p>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Email
                  </p>
                  <div className="mt-1">
                    <EmailPill email="info@maoqdigital.com" />
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Address
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-900 break-words">
                    42 Yellow Avens Blvd, Brampton, ON L6R 0T5
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/in/aumthakar"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 hover:border-blue-600 hover:text-blue-700 transition"
                  title="LinkedIn"
                >
                  <FaLinkedin size={18} />
                </a>
              </div>
            </Tile>
          </div>

          {/* Quick links + topics */}
          <div className="lg:col-span-4">
            <Tile title="Explore">
              <div className="grid grid-cols-2 gap-2">
                <Link to="/" className="hover:text-blue-700">Home</Link>
                <Link to="/listings" className="hover:text-blue-700">Listings</Link>
                <Link to="/about" className="hover:text-blue-700">About</Link>
                <Link to="/contact" className="hover:text-blue-700">Contact</Link>
                <Link to="/news" className="hover:text-blue-700">News</Link>
                <Link to="/jobs" className="hover:text-blue-700">Careers</Link>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Chip>Hotels</Chip>
                <Chip>Plazas</Chip>
                <Chip>Land</Chip>
                <Chip>Tech Consulting</Chip>
                <Chip>Data Centers</Chip>
                <Chip>Banking Investigations</Chip>
              </div>
            </Tile>
          </div>

          {/* Notes */}
          <div className="lg:col-span-3">
            <Tile title="Notes">
              <p>
                We share clean info and keep the process private. If a fit is there, we move quickly with clear steps.
              </p>
              <p className="mt-3 text-[12px] leading-relaxed text-slate-500">
                Maoq Digital Inc. provides strategic advisory and consulting across
                real estate, technology, and financial services; not a dealer in
                securities. Information shown is for general purposes only and not
                an offer to sell or solicit any security.
              </p>
            </Tile>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="px-4 sm:px-6 md:px-10 pt-8 pb-12">
        <div className="mx-auto max-w-7xl border-t border-slate-200 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Maoq Digital Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <Link to="/about" className="hover:text-blue-700">About</Link>
            <Link to="/contact" className="hover:text-blue-700">Contact</Link>
            <Link to="/listings" className="hover:text-blue-700">Listings</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;